import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import CompanySelect from '../../FashionOrders/SelectItem/CompanySelect';
import UserSelect from '../../Equipment/Select/UserSelect';
import SelectAndSearch from '../../../../components/SelectAndSearch';
import dreLineApi from '../../DreLines/api';
import {
  FINANCIAL_ENTRY_CATEGORY_OPTIONS,
  FINANCIAL_ENTRY_STATUS_OPTIONS,
  FINANCIAL_ENTRY_SUBCATEGORY_OPTIONS,
  FINANCIAL_ENTRY_TYPE,
  FINANCIAL_ENTRY_TYPE_OPTIONS,
  getSubcategoryLabel,
} from '../constants';
import FormOptionSelect from './FormOptionSelect';
import ItemFields from './ItemFields';
import { FinancialEntryFormSchemaType } from './schema';
import {
  CardHeaderRow,
  CardTitle,
  DropdownHeaderSelectedItems,
  DropdownSelectedItemTag,
  EntryTypeTag,
  EntryTypeTagGroup,
  FormGrid,
  FormSearchableSelectWrap,
  FullWidthFormGroup,
  StyledCard,
} from './style';

const GATEWAY = process.env.REACT_APP_GATEWAY_BASE_URL;
const COMPANIES_API = `${GATEWAY}/companies/companies`;
const USERS_API = `${GATEWAY}/backend/user/findAll`;

/** Filtros de empresa por lado: um lado é sempre um CNPJ próprio do tenant. */
const OWN_COMPANIES = { isMain: 'true' } as const;
const OTHER_COMPANIES = { excludeMainCompany: 'true' } as const;

interface CompanyOption {
  id: number;
  companyName: string;
}

interface UserOption {
  id: number;
  name: string;
}

const SUBCATEGORY_SEARCH_OPTIONS = FINANCIAL_ENTRY_SUBCATEGORY_OPTIONS.map(
  (option) => ({
    id: option.value,
    name: option.label,
  }),
);

const GeneralInfoSection: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FinancialEntryFormSchemaType>();
  const [userSelectKey, setUserSelectKey] = useState(0);
  const [dreLineOptions, setDreLineOptions] = useState<
    { id: number; name: string }[]
  >([]);

  const entryType = watch('type');
  const payerCompanyId = watch('payerCompanyId');
  const payerName = watch('payerName');
  const receiverCompanyId = watch('receiverCompanyId');
  const receiverName = watch('receiverName');
  const dreLineId = watch('dreLineId');
  const dreLineName = watch('dreLineName');
  const linkedUsers = watch('linkedUsers') ?? [];
  const subcategory = watch('subcategory');
  const subcategoryName =
    watch('subcategoryName') || getSubcategoryLabel(subcategory);
  const installments = watch('installments') ?? [];

  useEffect(() => {
    dreLineApi
      .list({ limit: 200, status: 'true' })
      .then((response) =>
        setDreLineOptions(
          response.data.map((line) => ({ id: line.id, name: line.name })),
        ),
      )
      .catch(() => setDreLineOptions([]));
  }, []);

  // Entrada: o dinheiro vem de fora e entra num CNPJ próprio. Saída: o inverso.
  const isExpense = entryType === FINANCIAL_ENTRY_TYPE.EXPENSE;
  const payerQueryParams = useMemo(
    () => (isExpense ? OWN_COMPANIES : OTHER_COMPANIES),
    [isExpense],
  );
  const receiverQueryParams = useMemo(
    () => (isExpense ? OTHER_COMPANIES : OWN_COMPANIES),
    [isExpense],
  );

  const handleAddLinkedUser = (item: UserOption | null) => {
    if (!item?.id) return;
    if (linkedUsers.some((user) => user.id === item.id)) {
      setUserSelectKey((key) => key + 1);
      return;
    }
    setValue(
      'linkedUsers',
      [...linkedUsers, { id: item.id, name: item.name }],
      { shouldValidate: true },
    );
    setUserSelectKey((key) => key + 1);
  };

  const handleRemoveLinkedUser = (userId: number) => {
    setValue(
      'linkedUsers',
      linkedUsers.filter((user) => user.id !== userId),
      { shouldValidate: true },
    );
  };

  const handleTypeChange = (value: number) => {
    if (value === entryType) return;
    setValue('type', value, { shouldValidate: true });
    // Os filtros de pagador/recebedor invertem com o tipo, então a seleção
    // anterior deixa de ser válida.
    setValue('payerCompanyId', null, { shouldValidate: true });
    setValue('payerName', '');
    setValue('receiverCompanyId', null, { shouldValidate: true });
    setValue('receiverName', '');

    if (value === FINANCIAL_ENTRY_TYPE.EXPENSE) {
      installments.forEach((_, index) => {
        setValue(`installments.${index}.hasCommission`, false, {
          shouldDirty: true,
        });
        setValue(`installments.${index}.commission`, '', { shouldDirty: true });
      });
    }
  };

  return (
    <StyledCard>
      <CardHeaderRow>
        <CardTitle style={{ margin: 0 }}>Geral</CardTitle>
        <Controller
          name="type"
          control={control}
          render={() => (
            <EntryTypeTagGroup
              role="group"
              aria-label="Tipo do lançamento"
              data-field-error={errors.type ? 'true' : undefined}
            >
              {FINANCIAL_ENTRY_TYPE_OPTIONS.map((option) => (
                <EntryTypeTag
                  key={option.value}
                  type="button"
                  $selected={entryType === option.value}
                  onClick={() => handleTypeChange(option.value)}
                >
                  {option.label}
                </EntryTypeTag>
              ))}
            </EntryTypeTagGroup>
          )}
        />
      </CardHeaderRow>

      <FormGrid cols={3}>
        <div>
          <Controller
            name="payerCompanyId"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.payerCompanyId}>
                <CompanySelect<CompanyOption>
                  key={`payer-${isExpense ? 'own' : 'other'}`}
                  label="Pagador"
                  apiUrl={COMPANIES_API}
                  queryParams={payerQueryParams}
                  displayField="companyName"
                  showDocument={false}
                  value={{
                    id: payerCompanyId ?? 0,
                    companyName: payerName ?? '',
                  }}
                  onChange={(item) => {
                    setValue('payerCompanyId', item.id > 0 ? item.id : null, {
                      shouldValidate: true,
                    });
                    setValue('payerName', item.companyName ?? '');
                  }}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="receiverCompanyId"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.receiverCompanyId}>
                <CompanySelect<CompanyOption>
                  key={`receiver-${isExpense ? 'other' : 'own'}`}
                  label="Recebedor"
                  apiUrl={COMPANIES_API}
                  queryParams={receiverQueryParams}
                  displayField="companyName"
                  showDocument={false}
                  value={{
                    id: receiverCompanyId ?? 0,
                    companyName: receiverName ?? '',
                  }}
                  onChange={(item) => {
                    setValue(
                      'receiverCompanyId',
                      item.id > 0 ? item.id : null,
                      { shouldValidate: true },
                    );
                    setValue('receiverName', item.companyName ?? '');
                  }}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="linkedUsers"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.linkedUsers}>
                <UserSelect<UserOption>
                  key={userSelectKey}
                  label="Usuário vinculado"
                  apiUrl={USERS_API}
                  displayField="name"
                  required
                  value={null}
                  onChange={handleAddLinkedUser}
                />
                <DropdownHeaderSelectedItems>
                  {linkedUsers.map((user) => (
                    <DropdownSelectedItemTag key={user.id}>
                      {user.name}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveLinkedUser(user.id);
                        }}
                      >
                        x
                      </button>
                    </DropdownSelectedItemTag>
                  ))}
                </DropdownHeaderSelectedItems>
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="dreLineId"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.dreLineId}>
                <SelectAndSearch
                  label="Linha do DRE"
                  options={dreLineOptions}
                  selectedOption={
                    dreLineId != null
                      ? { id: dreLineId, name: dreLineName || '' }
                      : null
                  }
                  onSelect={(option) => {
                    setValue('dreLineId', option.id ?? null, {
                      shouldValidate: true,
                    });
                    setValue('dreLineName', option.name ?? '');
                  }}
                  onClear={() => {
                    setValue('dreLineId', null, { shouldValidate: true });
                    setValue('dreLineName', '');
                  }}
                  placeholder="Selecione"
                  minChars={0}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Categoria"
                required
                value={field.value ?? null}
                onChange={field.onChange}
                options={FINANCIAL_ENTRY_CATEGORY_OPTIONS}
                hasError={!!errors.category}
              />
            )}
          />
        </div>

        <div>
          <Controller
            name="subcategory"
            control={control}
            render={() => (
              <FormSearchableSelectWrap $hasError={!!errors.subcategory}>
                <SelectAndSearch
                  label="Subcategoria"
                  options={SUBCATEGORY_SEARCH_OPTIONS}
                  selectedOption={
                    subcategory != null
                      ? { id: subcategory, name: subcategoryName || '' }
                      : null
                  }
                  onSelect={(option) => {
                    setValue('subcategory', option.id ?? null, {
                      shouldValidate: true,
                    });
                    setValue('subcategoryName', option.name ?? '');
                  }}
                  onClear={() => {
                    setValue('subcategory', null, { shouldValidate: true });
                    setValue('subcategoryName', '');
                  }}
                  placeholder="Selecione"
                  minChars={0}
                />
              </FormSearchableSelectWrap>
            )}
          />
        </div>

        <div>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormOptionSelect
                label="Status"
                required
                value={field.value ?? null}
                onChange={field.onChange}
                options={FINANCIAL_ENTRY_STATUS_OPTIONS}
                allowEmpty={false}
                hasError={!!errors.status}
              />
            )}
          />
        </div>

        <ItemFields />

        <FullWidthFormGroup>
          <label>Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <textarea {...field} placeholder="Descrição do lançamento" />
            )}
          />
        </FullWidthFormGroup>
      </FormGrid>
    </StyledCard>
  );
};

export default GeneralInfoSection;
